import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Type, AlignLeft, AlignCenter, AlignRight, Bold, Italic, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

const FONT_PAIRS = [
    { heading: 'Playfair Display', body: 'Source Sans Pro', style: 'Classic Elegance' },
    { heading: 'Montserrat', body: 'Open Sans', style: 'Modern Clean' },
    { heading: 'Bebas Neue', body: 'Roboto', style: 'Bold Impact' },
    { heading: 'Comic Neue', body: 'Quicksand', style: 'Friendly Fun' },
];

export const TypographyBasics = () => {
    const { completeModule } = useAcademyStore();
    const [selectedPair, setSelectedPair] = useState(0);
    const [headingSize, setHeadingSize] = useState(48);
    const [bodySize, setBodySize] = useState(16);
    const [lineHeight, setLineHeight] = useState(1.6);
    const [letterSpacing, setLetterSpacing] = useState(0);
    const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');
    const [showOverlay, setShowOverlay] = useState(false);
    const [experiments, setExperiments] = useState(0);
    const [completed, setCompleted] = useState(false);

    const currentPair = FONT_PAIRS[selectedPair];

    useEffect(() => {
        // Load Google Fonts
        FONT_PAIRS.forEach(pair => {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${pair.heading.replace(' ', '+')}:wght@700&family=${pair.body.replace(' ', '+')}:wght@400&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        });
    }, []);

    const handleChange = () => {
        setExperiments(e => e + 1);
    };

    useEffect(() => {
        if (experiments >= 5 && !completed) {
            setCompleted(true);
            completeModule('typography-basics', 100);
        }
    }, [experiments, completed, completeModule]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 font-sans">
            {/* Header */}
            <div className="bg-white shadow-sm px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-pink-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Complete</div>}
                    <div className="bg-pink-100 text-pink-800 px-4 py-1 rounded-full text-sm font-bold">
                        ✏️ Typography
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        <Type className="inline text-pink-600 mr-3" size={40} />
                        The Letter Painter
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Typography is the <strong>art of arranging text</strong>. Good font choices make
                        content readable, beautiful, and set the mood of your design!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Controls */}
                    <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100 space-y-6">
                        <div>
                            <h3 className="font-bold text-slate-700 mb-3">Font Pairing</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {FONT_PAIRS.map((pair, i) => (
                                    <button
                                        key={i}
                                        onClick={() => { setSelectedPair(i); handleChange(); }}
                                        className={`p-3 rounded-xl text-left border-2 transition-all ${selectedPair === i ? 'border-pink-500 bg-pink-50' : 'border-slate-200 hover:border-pink-200'
                                            }`}
                                    >
                                        <div className="text-xs text-slate-500">{pair.style}</div>
                                        <div className="font-bold text-sm text-slate-700">{pair.heading}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-bold text-slate-600">Heading Size</span>
                                <span className="font-mono text-pink-600">{headingSize}px</span>
                            </div>
                            <input
                                type="range" min="24" max="72" value={headingSize}
                                onChange={e => { setHeadingSize(parseInt(e.target.value)); handleChange(); }}
                                className="w-full accent-pink-500"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-bold text-slate-600">Body Size</span>
                                <span className="font-mono text-pink-600">{bodySize}px</span>
                            </div>
                            <input
                                type="range" min="12" max="24" value={bodySize}
                                onChange={e => { setBodySize(parseInt(e.target.value)); handleChange(); }}
                                className="w-full accent-pink-500"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-bold text-slate-600">Line Height</span>
                                <span className="font-mono text-pink-600">{lineHeight}</span>
                            </div>
                            <input
                                type="range" min="1" max="2.5" step="0.1" value={lineHeight}
                                onChange={e => { setLineHeight(parseFloat(e.target.value)); handleChange(); }}
                                className="w-full accent-pink-500"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-bold text-slate-600">Letter Spacing</span>
                                <span className="font-mono text-pink-600">{letterSpacing}px</span>
                            </div>
                            <input
                                type="range" min="-2" max="10" value={letterSpacing}
                                onChange={e => { setLetterSpacing(parseInt(e.target.value)); handleChange(); }}
                                className="w-full accent-pink-500"
                            />
                        </div>

                        <div>
                            <span className="text-sm font-bold text-slate-600 block mb-2">Text Align</span>
                            <div className="flex gap-2">
                                {(['left', 'center', 'right'] as const).map(align => (
                                    <button
                                        key={align}
                                        onClick={() => { setTextAlign(align); handleChange(); }}
                                        className={`flex-1 p-3 rounded-xl border-2 ${textAlign === align ? 'border-pink-500 bg-pink-50' : 'border-slate-200'
                                            }`}
                                    >
                                        {align === 'left' && <AlignLeft className="mx-auto" size={18} />}
                                        {align === 'center' && <AlignCenter className="mx-auto" size={18} />}
                                        {align === 'right' && <AlignRight className="mx-auto" size={18} />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100 relative">
                        <button
                            onClick={() => setShowOverlay(!showOverlay)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-pink-500"
                        >
                            {showOverlay ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>

                        <div className={`relative ${showOverlay ? 'bg-gradient-to-b from-pink-100/50 to-transparent' : ''}`}>
                            {showOverlay && (
                                <div className="absolute inset-0 pointer-events-none">
                                    <div className="h-px bg-pink-300 absolute top-0 w-full" />
                                    <div className="h-px bg-pink-300 absolute bottom-0 w-full" />
                                </div>
                            )}

                            <h1
                                style={{
                                    fontFamily: `"${currentPair.heading}", serif`,
                                    fontSize: `${headingSize}px`,
                                    letterSpacing: `${letterSpacing}px`,
                                    textAlign,
                                }}
                                className="font-bold text-slate-800 mb-6"
                            >
                                The Gorilla's Guide
                            </h1>

                            <p
                                style={{
                                    fontFamily: `"${currentPair.body}", sans-serif`,
                                    fontSize: `${bodySize}px`,
                                    lineHeight,
                                    textAlign,
                                }}
                                className="text-slate-600 mb-4"
                            >
                                Typography is more than just picking pretty fonts. It's about creating
                                a visual hierarchy that guides the reader's eye, establishes mood and tone,
                                and ensures readability across all devices.
                            </p>

                            <p
                                style={{
                                    fontFamily: `"${currentPair.body}", sans-serif`,
                                    fontSize: `${bodySize}px`,
                                    lineHeight,
                                    textAlign,
                                }}
                                className="text-slate-600"
                            >
                                The pairing of a <strong>display font</strong> for headings and a
                                <strong> readable font</strong> for body text is a foundational design skill.
                                Try different combinations to see how they change the feel of your content!
                            </p>
                        </div>

                        <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4 text-xs text-slate-500">
                            <div>
                                <strong className="text-slate-700">Heading:</strong> {currentPair.heading}
                            </div>
                            <div>
                                <strong className="text-slate-700">Body:</strong> {currentPair.body}
                            </div>
                        </div>

                        <div className="mt-4 text-xs text-slate-500 text-center">
                            💡 Make 5 changes to complete! Progress: {Math.min(experiments, 5)}/5
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TypographyBasics;
